import React, { useState, useEffect } from "react";
import decodeToken from "../../utils/decodeToken";
import SponsoredService from "../../services/sponsored";
import KidService from "../../services/kids";
import { filterKidsForSponsorListing } from "../../utils/kidAvailability";
import PaymentService from "../../services/payment";
import UserService from "../../services/user";
import { useNavigate } from "react-router-dom";
import Card from "../kids/components/Card";
import SponseredKidCard from "../sponseredKids/components/Card";
import moment from "moment";
import NoImg from "../../assets/no-image.png";
import Loader from "../../components/Loader";
import { FaPhone, FaEnvelope, FaEdit } from "react-icons/fa";
import KidProfileModal from "../kids/components/KidProfileModal";
import KidSponsorModal from "../kids/components/KidSponsorModal";
import { useSponsorResumeReopen } from "../../utils/sponsorResume";

import "./MentorProfile.css";

const MentorProfile = () => {
  let { user_id, super_admin_id, email } = decodeToken();
  const [sponsoredKids, setSponsoredKids] = useState([]);
  const [filterSponserKidsData, setFilterSponserKidsData] = useState([]);
  const [kids, setKids] = useState([]);
  const [rowData, setRowData] = useState([]);
  const [showFilter, setShowFilter] = useState(false);
  const [isViewAll, setIsViewAll] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("sponsored");
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showSponsorModal, setShowSponsorModal] = useState(false);
  const [selectedKidId, setSelectedKidId] = useState(null);

  useSponsorResumeReopen(setSelectedKidId, setShowSponsorModal);

  // Mentor profile data state
  const [mentorProfile, setMentorProfile] = useState({
    full_name: "",
    email: "",
    mobile_no: "",
    gender: "",
    avatar_url: "",
    age: "",
  });
  const [profileLoading, setProfileLoading] = useState(true);

  const navigate = useNavigate();

  // Function to validate image URL
  const getValidImageUrl = (imageUrl) => {
    // Return NoImg if imageUrl is null, undefined, or "null" string
    if (!imageUrl || imageUrl === "null" || imageUrl === "") {
      return NoImg;
    }
    return imageUrl;
  };

  // Function to calculate age from date of birth
  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return "";
    const birthYear = moment(dateOfBirth).year();
    const currentYear = moment().year();
    return `${currentYear - birthYear}`;
  };

  // Function to fetch mentor profile data
  const getMentorProfile = async () => {
    try {
      setProfileLoading(true);
      const { data } = await UserService.fetchUser({ user_id, super_admin_id });
      if (data.data && data.data.length > 0) {
        const profile = data.data[0];
        setMentorProfile({
          full_name: profile.full_name || "",
          email: profile.email || "",
          mobile_no: profile.mobile_no || "",
          gender: profile.gender || "",
          avatar_url: profile.avatar_url || "",
          age: calculateAge(profile.date_of_birth) || "",
        });
      }
    } catch (err) {
      console.log("Error fetching mentor profile:", err);
      // Set default values if API fails
      setMentorProfile({
        full_name: "Mentor Name",
        email: "",
        mobile_no: "",
        gender: "",
        avatar_url: "",
        age: "",
      });
    } finally {
      setProfileLoading(false);
    }
  };

  useEffect(() => {
    getMentorProfile();
    getAllSponsoredKids();
    getAllKids();
    getAllPayments();
  }, []);

  const getAllSponsoredKids = async () => {
    try {
      const { data } = super_admin_id
        ? await SponsoredService.fetchSponsorKidsByMentor(null, null, {
            allForSuperAdmin: true,
          })
        : await SponsoredService.fetchSponsorKidsByMentor(user_id, email);
      const rows = Array.isArray(data?.data) ? data.data : [];
      const sortedRows = [...rows].sort(
        (a, b) => new Date(b.mentored_on_date) - new Date(a.mentored_on_date),
      );

      const latestActiveByKid = new Map();
      const latestActiveKids = [];

      for (const kid of sortedRows) {
        if (kid.to_be_sponsor || !kid.is_active) continue;
        if (!latestActiveByKid.has(kid.kid_id)) {
          latestActiveByKid.set(kid.kid_id, kid);
          latestActiveKids.push(kid);
        }
      }

      setSponsoredKids(latestActiveKids);
      setLoading(false);
      return;
    } catch (err) {
      setSponsoredKids([]);
      console.log("Error fetching sponsored kids:", err);
      setLoading(false);
      return;
    }
  };

  const onViewProfile = (id) => {
    setSelectedKidId(id);
    setShowProfileModal(true);
  };

  const onSponsor = (id) => {
    setSelectedKidId(id);
    setShowSponsorModal(true);
  };

  let getAllKids = async () => {
    try {
      const { data } = await KidService.fetchKids({
        availableForSponsorship: true,
      });
      const filteredKids = filterKidsForSponsorListing(data.data);

      setKids(filteredKids);
      let statesArr = filteredKids
        .filter((k) => k.state)
        .map((s) => s.state);
      setFilterSponserKidsData(filteredKids);
      setLoading(false);
    } catch (err) {
      setLoading(false);
    }
  };

  const getAllPayments = async () => {
    try {
      const { data } = await PaymentService.fetchPaymentById(user_id);
      const payments = Array.isArray(data?.data) ? data.data : [];
      const sortedPayments = [...payments].sort(
        (a, b) =>
          parsePaymentDateForSort(b.payment_date) -
          parsePaymentDateForSort(a.payment_date),
      );
      setRowData(sortedPayments);
    } catch (err) {
      setRowData([]);
      console.log(err);
    }
  };

  const [allLoaded, setAllLoaded] = useState(false);

  const loadMoreTransactions = () => {
    setLoading(true);
    setTimeout(() => {
      setIsViewAll(true);
      setLoading(false);
    }, 2000);
  };

  const dataToShow = isViewAll ? rowData : rowData.slice(0, 5);

  const shuffleArray = (array) => {
    return array.sort(() => Math.random() - 0.5);
  };

  const onViewReceipt = (certificate_url, transaction_id) => {
    if (certificate_url) {
      // If certificate exists, open it
      window.open(certificate_url, "_blank");
    } else if (transaction_id) {
      // If no certificate yet, navigate to success page to generate it
      navigate(`/success/${transaction_id}`);
    }
  };

  const formatPaymentDate = (dateString) => {
    if (!dateString) return "";
    const formats = [
      "YYYY-MM-DD",
      "YYYY/MM/DD",
      "DD/MM/YYYY",
      "DD-MM-YYYY",
      "MM/DD/YYYY",
      moment.ISO_8601,
    ];
    const trimmedDate = String(dateString).trim();
    const parsed = moment(trimmedDate, formats, true);
    if (parsed.isValid()) {
      return parsed.format("DD/MM/YYYY");
    }

    const fallback = moment(trimmedDate);
    return fallback.isValid() ? fallback.format("DD/MM/YYYY") : "";
  };

  const parsePaymentDateForSort = (dateString) => {
    if (!dateString) return 0;
    const parsed = moment(
      String(dateString).trim(),
      [
        "YYYY-MM-DD",
        "YYYY/MM/DD",
        "DD/MM/YYYY",
        "DD-MM-YYYY",
        "MM/DD/YYYY",
        moment.ISO_8601,
      ],
      true,
    );
    if (parsed.isValid()) {
      return parsed.valueOf();
    }

    const fallback = moment(String(dateString).trim());
    return fallback.isValid() ? fallback.valueOf() : 0;
  };

  const handleViewAll = (section) => {
    if (section === "sponsored") {
      navigate("/sponsored-kids");
    } else if (section === "kids") {
      navigate("/kids");
    } else if (section === "transactions") {
      setIsViewAll(true);
    }
  };

  const handleView = () => {
    navigate("/all-kids");
  };

  const changeTab = (tab) => {
    setActiveTab(tab);
  };

  const handleEdit = () => {
    navigate("/mentor-profile");
  };

  const closeProfileModal = () => {
    setShowProfileModal(false);
    setSelectedKidId(null);
  };

  const closeSponsorModal = () => {
    setShowSponsorModal(false);
    setSelectedKidId(null);
  };

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <div style={{ marginTop: "4rem" }} className="mentor-profile-container">
          <div
            style={{
              zIndex: "-10000",
              backgroundColor: "#0c9f92",
              height: "170px",
            }}
          ></div>
          {/* New Layout with Sidebar */}
          <div className="profile-layout">
            {/* Sidebar with profile info */}
            <div className="profile-sidebar">
              <div className="profile-image-container">
                <img
                  src={getValidImageUrl(mentorProfile.avatar_url)}
                  alt="Profile"
                  className="profile-image"
                />
              </div>
              <h2 className="profile-name">
                {mentorProfile.full_name || "Mentor Name"}
              </h2>
              <p className="profile-meta">
                {mentorProfile.age && mentorProfile.gender
                  ? `${mentorProfile.age} • ${mentorProfile.gender}`
                  : mentorProfile.age
                    ? `${mentorProfile.age} years`
                    : mentorProfile.gender
                      ? mentorProfile.gender
                      : "Age • Gender"}
              </p>

              <div className="profile-contact">
                {mentorProfile.mobile_no && (
                  <div className="contact-item">
                    <FaPhone className="contact-icon" />
                    <span>{mentorProfile.mobile_no}</span>
                  </div>
                )}
                {mentorProfile.email && (
                  <div className="contact-item">
                    <FaEnvelope className="contact-icon" />
                    <span>{mentorProfile.email}</span>
                  </div>
                )}
              </div>

              <button className="edit-profile-btn" onClick={handleEdit}>
                Edit Profile
              </button>
            </div>

            {/* Main content */}
            <div className="profile-content">
              {/* Tabs */}
              <div className="profile-tabs">
                <button
                  style={{ flex: "1" }}
                  className={`tab-btn ${activeTab === "sponsored" ? "active" : ""}`}
                  onClick={() => changeTab("sponsored")}
                >
                  Sponsored Kids
                </button>
                <button
                  style={{ flex: "1" }}
                  className={`tab-btn ${activeTab === "transaction" ? "active" : ""}`}
                  onClick={() => changeTab("transaction")}
                >
                  Transaction History
                </button>
                <button
                  style={{ flex: "1" }}
                  className={`tab-btn ${activeTab === "explore" ? "active" : ""}`}
                  onClick={() => changeTab("explore")}
                >
                  Explore
                </button>
              </div>

              {/* Tab Content */}
              <div className="tab-content">
                {/* Sponsored Kids Section */}
                <div
                  className={`tab-pane ${activeTab === "sponsored" ? "active" : ""}`}
                >
                  {sponsoredKids?.length > 0 ? (
                    <div className="sponsored-grid">
                      {sponsoredKids.map((kid) => (
                        <SponseredKidCard
                          key={kid.id}
                          user_id={user_id}
                          super_admin_id={super_admin_id}
                          onViewProfile={onViewProfile}
                          onSponsor={onSponsor}
                          data={kid}
                          style={{
                            width: "calc(25% - 15px)",
                            minWidth: "220px",
                            margin: "0",
                          }}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="no-data-msg">
                      <p>No sponsored kids found</p>
                    </div>
                  )}
                  <div className="see-all-container">
                    <button
                      style={{ backgroundColor: "black", color: "white" }}
                      className="see-all-btn"
                      onClick={() => handleViewAll("sponsored")}
                    >
                      See All {sponsoredKids.length}
                    </button>
                  </div>
                </div>

                {/* Transaction History */}
                <div
                  className={`tab-pane ${activeTab === "transaction" ? "active" : ""}`}
                >
                  {dataToShow.length > 0 ? (
                    <div className="transaction-table-wrapper">
                      <table className="transaction-table">
                        <thead>
                          <tr>
                            <th className="name-column">Name</th>
                            <th className="amount-column">Amount</th>
                            <th className="date-column">Date</th>
                            <th className="transaction-id-column">
                              Transaction id
                            </th>
                            <th className="status-column">Status</th>
                            <th className="action-column">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {dataToShow.map((data, idx) => (
                            <tr key={idx}>
                              <td className="name-column">{data.name}</td>
                              <td className="amount-column">{data.amount}</td>
                              <td className="date-column">
                                {formatPaymentDate(data.payment_date)}
                              </td>
                              <td className="transaction-id-column">
                                {data.transaction_id}
                              </td>
                              <td className="status-column">
                                <span className="status-badge">Paid</span>
                              </td>
                              <td className="action-column">
                                <button
                                  className="view-receipt-btn"
                                  onClick={() =>
                                    onViewReceipt(
                                      data.certificate_url,
                                      data.transaction_id,
                                    )
                                  }
                                >
                                  View Receipt
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="no-data-msg">
                      <p>No payment history found</p>
                    </div>
                  )}

                  {!isViewAll && rowData.length > 5 && (
                    <div className="see-all-container">
                      <button
                        className="see-all-btn"
                        style={{
                          backgroundColor: "#0c9f92",
                          color: "#ffffff",
                          borderRadius: "20px",
                          padding: "8px 20px",
                          border: "none",
                        }}
                        onClick={() => handleViewAll("transactions")}
                      >
                        See All
                      </button>
                    </div>
                  )}
                </div>

                {/* Explore Section - NEW */}
                <div
                  className={`tab-pane ${activeTab === "explore" ? "active" : ""}`}
                >
                  {kids?.length > 0 ? (
                    <div className="explore-grid">
                      <div className="explore-header">
                        <h3 className="explore-title">
                          Discover New Kids to Sponsor ✨
                        </h3>
                        <p className="explore-subtitle">
                          Find children who need your support and make a
                          difference in their lives
                        </p>
                      </div>
                      <div className="explore-cards-grid">
                        {shuffleArray([...kids])
                          .slice(0, 8)
                          .map((kid) => (
                            <div className="explore-card-item" key={kid.id}>
                              <Card
                                onViewProfile={onViewProfile}
                                onSponsor={onSponsor}
                                data={kid}
                              />
                            </div>
                          ))}
                      </div>
                    </div>
                  ) : (
                    <div className="no-data-msg">
                      <p>No kids available for sponsorship at the moment</p>
                    </div>
                  )}
                  <div className="see-all-container">
                    <button
                      style={{ backgroundColor: "#0c9f92", color: "white" }}
                      className="see-all-btn"
                      onClick={handleView}
                    >
                      Explore All Kids
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Section - Stars You'd Love To Shine A Light On */}
          <div className="sponsor-section">
            <h2
              style={{
                fontSize: "24px",
                fontWeight: "500",
                paddingLeft: "3rem",
                fontFamily: "Poppins",
              }}
              className="section-title"
            >
              Stars You'd Love To Shine A Light On ✨
            </h2>
            <div className="sponsor-grid">
              {filterSponserKidsData?.length > 0 &&
                shuffleArray([...filterSponserKidsData])
                  .slice(0, 4)
                  .map((kid, idx) => (
                    <div key={idx} className="sponsor-card-item">
                      <Card
                        onViewProfile={onViewProfile}
                        onSponsor={onSponsor}
                        data={kid}
                      />
                    </div>
                  ))}
            </div>
            <div className="see-all-container">
              <button
                style={{
                  backgroundColor: "black",
                  color: "white",
                  padding: "0.5rem 1rem",
                  borderRadius: "2rem",
                  marginTop: "-3rem",
                }}
                className="view-more-btn"
                onClick={handleView}
              >
                View More
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Kid Profile Modal */}
      <KidProfileModal
        isOpen={showProfileModal}
        onClose={closeProfileModal}
        kidId={selectedKidId}
        onSponsorClick={(id) => {
          closeProfileModal();
          setShowSponsorModal(true);
          setSelectedKidId(id);
        }}
      />

      {/* Kid Sponsor Modal */}
      <KidSponsorModal
        isOpen={showSponsorModal}
        onClose={() => {
          setShowSponsorModal(false);
          setSelectedKidId(null);
        }}
        kidId={selectedKidId}
      />

      <style>{`
      @media (max-width: 768px){
        .mentor-profile-container {
          min-height: 100vh;
          background-color: #f8f9fa;
        }

        .profile-layout {
          display: flex;
          margin: 0 auto;
          gap: 2rem;
          padding: 2rem;
          margin-top: -80px;
          position: relative;
          z-index: 1;
        }

        .profile-sidebar {
          background: white;
          border-radius: 12px;
          padding: 2rem;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
          height: fit-content;
          min-width: 280px;
          position: sticky;
          top: 2rem;
        }

        .profile-image-container {
          text-align: center;
          margin-bottom:-2rem;
        }

        .profile-image {
          width: 120px;
          height: 120px;
          border-radius: 100%;
          object-fit: cover;
          border: 4px solid #0c9f92;
        }

        .profile-name {
          text-align: center;
          margin-bottom: -2rem;
          color: #333;
          font-size: 1.5rem;
          font-weight: 600;
        }

        .profile-meta {
          text-align: center;
          color: #666;
          margin-bottom: 0rem;
          font-size: 0.9rem;
        }

        .profile-contact {
          margin-bottom: 1rem;
      }

        .contact-item {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          margin-bottom: 0.5rem;
          color: #555;
          justify-content: flex-start;
          font-size: 0.9rem;
        }

        .contact-icon {
          color: #0c9f92;
          font-size: 1.1rem;
        }

        .edit-profile-btn {
          width: 100%;
          background: #0c9f92;
          color: white;
          border: none;
          padding: 0.75rem 1.5rem;
          border-radius: 25px;
          font-weight: 500;
          cursor: pointer;
          transition: background-color 0.3s ease;
        }

        .edit-profile-btn:hover {
          background: #0a8a7d;
        }

        .profile-content {
          flex: 1;
          background: white;
          border-radius: 12px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
        }

        .profile-tabs {
          display: flex;
          border-bottom: 2px solid #f0f0f0;
        }

        .tab-btn {
          background: none;
          border: none;
          padding: 1rem 2rem;
          cursor: pointer;
          font-weight: 500;
          color: #666;
          border-bottom: 2px solid transparent;
          transition: all 0.3s ease;
        }

        .tab-btn:hover {
          color: #0c9f92;
          background-color: #f8f9fa;
        }

        .tab-btn.active {
          color: #0c9f92;
          border-bottom-color: #0c9f92;
          background-color: #f0fffe;
        }

        .tab-content {
          padding: 2rem;
        }

        .tab-pane {
          display: none;
        }

        .tab-pane.active {
          display: block;
        }

        .sponsored-grid,
        .explore-cards-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
          gap: 2rem;
          margin-bottom: 2rem;
          padding: 0 1rem;
               display: flex;
            justify-content: center;
        }

        .explore-grid {
          margin-bottom: 2rem;
        }

        .explore-header {
          text-align: center;
          margin-bottom: 2rem;
        }

        .explore-title {
          font-size: 1.8rem;
          font-weight: 600;
          color: #333;
          margin-bottom: 0.5rem;
        }

        .explore-subtitle {
          color: #666;
          font-size: 1.1rem;
        }

        .no-data-msg {
          text-align: center;
          padding: 3rem;
          color: #666;
        }

        .no-data-msg p {
          font-size: 1.1rem;
          margin: 0;
        }

        .see-all-container {
          text-align: center;
          margin-top: 2rem;
        }

        .see-all-btn {
          background: #0c9f92;
          color: white;
          border: none;
          padding: 0.75rem 2rem;
          border-radius: 25px;
          font-weight: 500;
          cursor: pointer;
          transition: background-color 0.3s ease;
        }

        .see-all-btn:hover {
          background: #0a8a7d;
        }

        .transaction-table-wrapper {
          overflow-x: auto;
          margin-bottom: 2rem;
        }

        .transaction-table {
          width: 100%;
          border-collapse: collapse;
          margin-bottom: 1rem;
        }

        .transaction-table th,
        .transaction-table td {
          padding: 1rem;
          text-align: left;
          border-bottom: 1px solid #e9ecef;
        }

        .transaction-table th {
          background-color: #f8f9fa;
          font-weight: 600;
          color: #333;
        }

        .status-badge {
          background: #28a745;
          color: white;
          padding: 0.25rem 0.75rem;
          border-radius: 12px;
          font-size: 0.875rem;
          font-weight: 500;
        }

        .view-receipt-btn {
          background: #007bff;
          color: white;
          border: none;
          padding: 0.5rem 1rem;
          border-radius: 6px;
          cursor: pointer;
          font-size: 0.875rem;
          transition: background-color 0.3s ease;
        }

        .view-receipt-btn:hover {
          background: #0056b3;
        }

        .sponsor-section {
          max-width: 1200px;
          margin: 0 auto;
          padding: 2rem;
        }

        .section-title {
          text-align: center;
          margin-bottom: 2rem;
          color: #333;
        }

        .sponsor-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 2rem;
          margin-bottom: 2rem;
          padding: 0 1rem;
        }

        .sponsor-card-item {
          display: flex;
          justify-content: center;
        }

        .view-more-btn {
          background: #333;
          color: white;
          border: none;
          cursor: pointer;
          transition: background-color 0.3s ease;
        }

        .view-more-btn:hover {
          background: #555;
        }

        /* Mobile Responsiveness */
        @media (max-width: 768px) {
          .profile-layout {
            flex-direction: column;
            padding: 1rem;
            margin-top: -40px;
            
          }

          .profile-sidebar {
            min-width: auto;
            position: static;
          }

          .tab-btn {
            padding: 0.75rem 1rem;
            font-size: 0.9rem;
          }

          .tab-content {
            padding: 1rem;
          }

          .sponsored-grid,
          .explore-cards-grid {
            grid-template-columns: 1fr;
            gap: 1rem;
            flex-direction: column;
            align-items: center;
            justify-content: center;
                display: flex;
            justify-content: center;
          }

          .sponsor-grid {
            grid-template-columns: 1fr;
            display: flex;
            justify-content: center;
          }

          .sponsor-grid > div {
            width: 100% !important;
            max-width: 320px !important;
          }

          .section-title {
            font-size: 1.5rem !important;
            padding-left: 1rem !important;
            text-align: center;
          }

          .transaction-table {
            font-size: 0.875rem;
          }

          .transaction-table th,
          .transaction-table td {
            padding: 0.5rem;
          }
        }

        @media (max-width: 480px) {
          .profile-layout {
            padding: 0.5rem;
          }

          .profile-sidebar {
            padding: 1.5rem;
          }

          .profile-content {
            margin-top: 1rem;
          }

          .tab-btn {
            padding: 0.5rem 0.75rem;
            font-size: 0.8rem;
          }

          .explore-title {
            font-size: 1.5rem;
          }

          .explore-subtitle {
            font-size: 1rem;
          }
        }
      }
      .explore-cards-grid {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 2rem;
        margin-bottom: 2rem;
        padding: 0 1rem;
        
      }
      .explore-card-item {
        display: flex;
        justify-content: center;
      }
      @media (max-width: 1024px) {
        .explore-cards-grid {
          grid-template-columns: repeat(2, 1fr);
          
        }
      }
      @media (max-width: 600px) {
        .explore-cards-grid {
          grid-template-columns: 1fr;
          
        }
        .explore-card-item {
          margin-bottom: 1.5rem;
          
        }
      }
      `}</style>
    </>
  );
};

export default MentorProfile;
